import { useEffect, useRef } from 'react';
import enviro from 'enviro';
import Raven from 'raven-js';
import I18n from 'I18n';
import { openPopup, openTab } from '../../utils/popupUtils';
import { getAbsoluteReconnectUrl } from '../../utils/urlUtils';
import { getPopupUrl } from '../../utils/getPopupUrl';
const FIVE_MINUTES = 300000;
const DELAY_500_MS = 500;
export const URLServiceComponent = ({
  metadata,
  onAuthCancelled,
  onAuthFailed,
  onAuthSucceeded,
  reconnectStarted,
  url: urlTemplate,
  timeout = FIVE_MINUTES
}) => {
  const appId = metadata === null || metadata === void 0 ? void 0 : metadata.appId;
  const accountId = metadata === null || metadata === void 0 ? void 0 : metadata.accountId;
  const businessUnitId = metadata === null || metadata === void 0 ? void 0 : metadata.businessUnitId;
  const reconnectUrl = getAbsoluteReconnectUrl({
    urlTemplate,
    appId,
    accountId,
    businessUnitId
  });
  const popupRef = useRef(null);
  const authStartedRef = useRef(false);
  useEffect(() => {
    if (reconnectUrl && reconnectStarted && !authStartedRef.current) {
      authStartedRef.current = true;
      let timeoutId = 0;
      let intervalId = 0;
      const clearTimers = () => {
        clearTimeout(timeoutId);
        clearInterval(intervalId);
      };
      const handleAuthCancelled = ({
        message
      }) => {
        clearTimers();
        const messageText = message || I18n.text('reconnect.warnings.reconnectionCancelled');
        onAuthCancelled === null || onAuthCancelled === void 0 || onAuthCancelled({
          message: messageText
        });
        authStartedRef.current = false;
      };
      const handleAuthFailed = ({
        error,
        errorMessage,
        queryParams
      }) => {
        clearTimers();
        onAuthFailed === null || onAuthFailed === void 0 || onAuthFailed({
          error,
          errorMessage,
          queryParams
        });
        authStartedRef.current = false;
      };
      const handleAuthSucceeded = authData => {
        clearTimers();
        onAuthSucceeded === null || onAuthSucceeded === void 0 || onAuthSucceeded(authData);
        authStartedRef.current = false;
      };
      const handleAuth = ({
        code,
        error,
        errorMessage,
        queryParams
      }) => {
        if (error) {
          handleAuthFailed({
            error,
            errorMessage,
            queryParams
          });
        } else if (!code) {
          handleAuthCancelled({
            message: errorMessage
          });
        } else {
          handleAuthSucceeded(code);
        }
      };
      const openPopupWindow = popupWindowUrl => {
        //@ts-ignore authCallback does not exist on type 'Window & typeof globalThis'
        window.authCallback = handleAuth;
        //@ts-ignore authCancelled does not exist on type 'Window & typeof globalThis'
        window.authCancelled = handleAuthCancelled;
        //@ts-ignore authCancelled does not exist on type 'Window & typeof globalThis'
        window.authFailed = handleAuthFailed;
        popupRef.current = enviro.deployed() ? openPopup(popupWindowUrl) : openTab(popupWindowUrl);
        if (popupRef.current) {
          timeoutId = setTimeout(() => {
            handleAuthFailed({
              error: new Error('Reconnecting timeout'),
              errorMessage: I18n.text('reconnect.errors.reconnectFailedDueToTimeout')
            });
          }, timeout);
          intervalId = setInterval(() => {
            let popupIsClosed = !popupRef.current;
            try {
              /**
               * The workaround in case of Cross-Origin-Opener-Policy problem
               */
              popupIsClosed = popupRef.current.closed;
            } catch (err) {
              // do nothing
            }
            if (popupIsClosed) {
              handleAuthCancelled({});
            }
          }, DELAY_500_MS);
        } else {
          throw new Error('Unable to open popup window');
        }
      };
      void getPopupUrl({
        url: reconnectUrl
      }).then(popupUrl => {
        if (popupUrl) {
          openPopupWindow(popupUrl);
        } else {
          throw new Error(`Unable to fetch authorize URL using: ${reconnectUrl}`);
        }
      }).catch(err => {
        if (!enviro.deployed()) {
          console.error(err);
        }
        Raven.captureException(err);
        handleAuthFailed({
          error: err,
          errorMessage: I18n.text('reconnect.errors.reconnectFailed')
        });
      });
    }
  }, [reconnectUrl, reconnectStarted, onAuthCancelled, onAuthFailed, onAuthSucceeded, timeout]);
  return null;
};